import React, { useEffect, useState } from 'react';
import { CheckCircle, Circle, Clock, AlertCircle, Calendar, Tag, ChevronDown, ChevronRight, Plus, X, Timer, XCircle } from 'lucide-react';
import type { Todo } from '../types';
import { TaskStatus } from '../types';
import { formatDateForDisplay } from '../utils/dateUtils';
import { AIQuickActions } from './ai/AIQuickActions';
import { useAuth } from '../contexts/AuthContext';

interface TodoItemProps {
  todo: Todo;
  onClick: () => void;
  onStatusChange: (status: TaskStatus) => void;
  onSubtaskStatusChange?: (subtaskId: string, status: TaskStatus) => void;
  onAddChild?: (parentId: string) => void;
  onSubtaskClick?: (subtask: Todo) => void;
  onMoveFromToday?: (todoId: string) => void;
  onMakeChild?: (childId: string, parentId: string) => void;
  onDelete?: (todoId: string) => void;
  compact?: boolean;
  parentTodo?: Todo; // For showing parent information
  allTodos?: Todo[]; // For resolving parent references
  depth?: number; // For nested indentation
  isDraggable?: boolean;
  isInTodaySection?: boolean;
  teamMembers?: { id: string; name: string; email: string; }[]; // Team members for displaying assignee names
  isPersonalMode?: boolean; // Whether we're in personal team mode
  // Removed hideActiveWeekIndicator - no longer needed
  onStartPomodoro?: (todo: Todo) => void; // Callback for starting Pomodoro timer
  onSchedule?: (todo: Todo) => void; // Callback for scheduling todo as calendar event
  nextEventTime?: string; // Next scheduled event time for this todo
}

const TodoItemComponent: React.FC<TodoItemProps> = ({ 
  todo, 
  onClick, 
  onStatusChange, 
  onSubtaskStatusChange,
  onAddChild, 
  onSubtaskClick, 
  onMoveFromToday,
  onMakeChild,
  onDelete,
  compact = false,
  parentTodo,
  allTodos = [],
  depth = 0,
  isDraggable = false,
  isInTodaySection = false,
  teamMembers = [],
  isPersonalMode = false,
  onStartPomodoro,
  onSchedule,
  nextEventTime
}) => {
  const { user } = useAuth();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDraggedOver, setIsDraggedOver] = useState(false);
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [statusMenuPosition, setStatusMenuPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [isHoveredAddChild, setIsHoveredAddChild] = useState(false);

  // Get subtasks from allTodos instead of making API calls
  const subtasks = allTodos.filter(t => t.parent_id === todo.id);
  const hasSubtasks = subtasks.length > 0;
  const resolvedParentTodo = parentTodo || allTodos.find(t => t.id === todo.parent_id);

  // Helper function to get assignee name
  const getAssigneeName = (assigneeId?: string) => {
    if (!assigneeId || !teamMembers.length) return null;
    const member = teamMembers.find(m => m.id === assigneeId);
    return member?.name || assigneeId;
  };

  // No need to fetch subtasks - we get them from allTodos

  const handleToggleExpand = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  const handleAddChild = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onAddChild) {
      onAddChild(todo.id);
    }
  };

  const handleRemoveFromToday = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onMoveFromToday) {
      onMoveFromToday(todo.id);
    }
  };

  const handleSubtaskClick = (subtask: Todo) => {
    // This will open the subtask itself, not the parent
    if (onSubtaskClick) {
      onSubtaskClick(subtask);
    } else {
      // Fallback to opening parent if no specific handler provided
      onClick();
    }
  };

  const getStatusIcon = (status: TaskStatus) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'in_progress':
        return <Clock className="w-5 h-5 text-blue-600" />;
      case 'waiting':
        return <AlertCircle className="w-5 h-5 text-yellow-600" />;
      case 'canceled':
        return <XCircle className="w-5 h-5 text-red-400" />;
      default:
        return <Circle className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusClass = (status: TaskStatus) => {
    switch (status) {
      case 'completed':
        return 'status-completed';
      case 'in_progress':
        return 'status-in-progress';
      default:
        return 'status-not-started';
    }
  };

  const formatDate = (dateString?: string) => {
    return formatDateForDisplay(dateString);
  };

  const getNextStatus = (currentStatus: TaskStatus): TaskStatus => {
    switch (currentStatus) {
      case TaskStatus.NOT_STARTED:
        return TaskStatus.IN_PROGRESS;
      case TaskStatus.IN_PROGRESS:
        return TaskStatus.COMPLETED;
      case TaskStatus.WAITING:
        return TaskStatus.IN_PROGRESS;
      case TaskStatus.COMPLETED:
        return TaskStatus.NOT_STARTED;
      case TaskStatus.CANCELED:
        return TaskStatus.NOT_STARTED;
      default:
        return TaskStatus.IN_PROGRESS;
    }
  };

  const getStatusTooltip = (currentStatus: TaskStatus): string => {
    const nextStatus = getNextStatus(currentStatus);
    const statusNames = {
      [TaskStatus.NOT_STARTED]: 'Not Started',
      [TaskStatus.IN_PROGRESS]: 'In Progress',
      [TaskStatus.WAITING]: 'Waiting',
      [TaskStatus.COMPLETED]: 'Completed',
      [TaskStatus.CANCELED]: 'Canceled'
    };
    return `Current: ${statusNames[currentStatus]} | Click: ${statusNames[nextStatus]} | Right-click: More options`;
  };

  const handleStatusClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const nextStatus = getNextStatus(todo.status);
    onStatusChange(nextStatus);
  };

  const handleStatusRightClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setStatusMenuPosition({ x: e.clientX, y: e.clientY });
    setShowStatusMenu(true);
  };

  const handleStatusSelect = (status: TaskStatus) => {
    setShowStatusMenu(false);
    if (status !== todo.status) {
      onStatusChange(status);
    }
  };

  // Close status menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setShowStatusMenu(false);
    if (showStatusMenu) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showStatusMenu]);

  const handleSubtaskStatusChange = async (subtaskId: string, status: TaskStatus) => {
    if (onSubtaskStatusChange) {
      onSubtaskStatusChange(subtaskId, status);
    }
  };

  const handleDragStart = (e: React.DragEvent) => {
    if (!isDraggable) return;
    
    setIsDragging(true);
    
    // Set the drag data - use both text/plain and Text for compatibility
    e.dataTransfer.setData('text/plain', todo.id);
    e.dataTransfer.setData('Text', todo.id); // Capital T for older browsers
    e.dataTransfer.effectAllowed = 'copy';
    
    // Add visual feedback for tasks that can't be moved due to due dates
    if (todo.deadline) {
      const deadline = new Date(todo.deadline);
      const today = new Date();
      today.setHours(23, 59, 59, 999);
      
      if (deadline <= today) {
        e.dataTransfer.effectAllowed = 'none';
      }
    }
  };

  const handleDragEnd = () => {
    setIsDraggedOver(false);
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    // Only handle drag over if we have a makeChild handler and the drag is from another TodoItem
    if (!onMakeChild) return;
    
    e.preventDefault();
    
    e.dataTransfer.dropEffect = 'move';
    setIsDraggedOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    if (!onMakeChild) return;
    
    e.preventDefault();
    // Only clear if we're actually leaving this element
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDraggedOver(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    if (!onMakeChild) return;
    
    e.preventDefault();
    setIsDraggedOver(false);
    
    const draggedId = e.dataTransfer.getData('text/plain');
    
    // Don't allow dropping on self
    if (draggedId === todo.id) return;
    
    // Don't allow dropping a parent on its child (prevent circular relationships)
    const draggedTodo = allTodos.find(t => t.id === draggedId);
    if (draggedTodo && isDescendantOf(todo, draggedTodo)) return;
    
    // Stop propagation only if we're actually handling the drop
    e.stopPropagation();
    onMakeChild(draggedId, todo.id);
  };

  // Helper function to check if a todo is a descendant of another todo
  const isDescendantOf = (potentialChild: Todo, potentialParent: Todo, visited = new Set<string>()): boolean => {
    // Prevent infinite recursion
    if (visited.has(potentialParent.id)) {
      return false;
    }
    
    if (potentialChild.id === potentialParent.id) return true;
    
    // Add current parent to visited set
    visited.add(potentialParent.id);
    
    // Find all children of potentialParent
    const children = allTodos.filter(t => t.parent_id === potentialParent.id);
    
    // Check if potentialChild is among the children or their descendants
    const result = children.some(child => 
      child.id === potentialChild.id || isDescendantOf(potentialChild, child, visited)
    );
    
    // Remove from visited set when backtracking
    visited.delete(potentialParent.id);
    
    return result;
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDelete && window.confirm('Are you sure you want to delete this task? This action cannot be undone.')) {
      onDelete(todo.id);
    }
  };


  const indentationStyle = depth > 0 ? { marginLeft: `${depth * 24}px` } : {};

  return (
    <div className={`${getStatusClass(todo.status)}`} style={indentationStyle}>
      <div
        className={`card cursor-pointer hover:shadow-md transition-shadow ${
          compact ? 'p-3 sm:p-4' : 'p-4 sm:p-6'
        } ${depth > 0 ? 'border-l-4 border-gray-200' : ''} ${
          isDraggable ? 'cursor-move' : ''
        } ${isDraggedOver ? 'bg-blue-50 border-2 border-blue-400 border-dashed' : ''} relative`}
        onClick={() => {
          // Don't open the todo form if we just finished dragging
          if (!isDragging) {
            onClick();
          }
        }}
        draggable={isDraggable}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {/* Delete Button - Top Left */}
        {onDelete && (
          <button
            onClick={handleDelete}
            className="absolute top-2 left-2 text-gray-400 hover:text-red-600 hover:bg-red-50 p-1 rounded-full transition-colors z-10"
            title="Delete task"
          >
            <X className="w-4 h-4" />
          </button>
        )}
        
        <div className="flex items-center space-x-2 sm:space-x-3">
          {/* Expand/Collapse Button for Subtasks */}
          {hasSubtasks && (
            <button
              onClick={handleToggleExpand}
              className="flex-shrink-0 hover:bg-gray-100 p-1.5 sm:p-1 rounded transition-colors touch-manipulation"
            >
              {isExpanded ? (
                <ChevronDown className="w-4 h-4 text-gray-600" />
              ) : (
                <ChevronRight className="w-4 h-4 text-gray-600" />
              )}
            </button>
          )}
          
          <button
            onClick={handleStatusClick}
            onContextMenu={handleStatusRightClick}
            className="flex-shrink-0 hover:scale-110 transition-transform p-1 sm:p-0 touch-manipulation"
            title={getStatusTooltip(todo.status)}
          >
            {getStatusIcon(todo.status)}
          </button>
          
          <div className="flex-1 min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
              <div className="flex-1 min-w-0">
                <h3 className={`font-medium ${
                  todo.title.length > 30 ? 'text-xs' : 
                  todo.title.length > 20 ? 'text-sm' : 
                  todo.title.length > 10 ? 'text-sm sm:text-base' :
                  'text-base sm:text-lg'
                } ${todo.status === 'completed' || todo.status === 'canceled' ? 'line-through text-gray-500' : ''}`}>
                  {todo.title}
                  {resolvedParentTodo && (
                    <span className="ml-2 text-xs text-gray-500">
                      Subtask of: {resolvedParentTodo.title}
                    </span>
                  )}
                </h3>
              </div>
              
              <div className="flex items-center justify-between sm:justify-end space-x-2">
                {todo.horizon && (
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    todo.horizon === 'now' ? 'bg-red-100 text-red-800' :
                    todo.horizon === 'next' ? 'bg-blue-100 text-blue-800' :
                    todo.horizon === 'soon' ? 'bg-yellow-100 text-yellow-800' :
                    todo.horizon === 'later' ? 'bg-gray-100 text-gray-600' :
                    'bg-gray-100 text-gray-500'
                  }`}>
                    {todo.horizon}
                  </span>
                )}
                
                <div className="flex items-center space-x-1">
                  {/* AI Quick Actions */}
                  {user?.preferences?.aiFeaturesEnabled === true && (
                    <AIQuickActions
                      todo={todo}
                      onPriorityChanged={() => {
                        // AI priority suggestion - will be connected to task update
                      }}
                      onDeadlineChanged={() => {
                        // AI deadline suggestion - will be connected to task update
                      }}
                    />
                  )}
                  {/* Pomodoro Timer Button */}
                  {onStartPomodoro && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onStartPomodoro(todo);
                      }}
                      className="text-gray-400 hover:text-green-600 hover:bg-green-50 p-1.5 sm:p-1 rounded transition-colors touch-manipulation"
                      title="Start Pomodoro timer for this task"
                    >
                      <Timer className="w-4 h-4" />
                    </button>
                  )}
                  {/* Schedule as Event Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onSchedule) {
                        onSchedule(todo);
                      } else {
                        window.dispatchEvent(new CustomEvent('scheduleTodoEvent', { detail: { todo } }));
                      }
                    }}
                    className="text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 p-1.5 sm:p-1 rounded transition-colors touch-manipulation"
                    title="Schedule this task"
                  >
                    <Calendar className="w-4 h-4" />
                  </button>
                  {/* Add Child Button */}
                  {onAddChild && (
                    <button
                      onClick={handleAddChild}
                      onMouseEnter={() => setIsHoveredAddChild(true)}
                      onMouseLeave={() => setIsHoveredAddChild(false)}
                      className="text-gray-400 hover:text-blue-600 hover:bg-blue-50 p-1.5 sm:p-1 rounded transition-all duration-200 touch-manipulation"
                      title="Add subtask to this task"
                    >
                      {isHoveredAddChild ? (
                        <X className="w-4 h-4 transform rotate-45 transition-transform duration-200" />
                      ) : (
                        <Plus className="w-4 h-4 transition-transform duration-200" />
                      )}
                    </button>
                  )}
                  {/* Remove from Today Button */}
                  {isInTodaySection && onMoveFromToday && (
                    <button
                      onClick={handleRemoveFromToday}
                      className="text-gray-400 hover:text-red-600 hover:bg-red-50 p-1.5 sm:p-1 rounded transition-colors touch-manipulation"
                      title="Remove from Today"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
            
            {!compact && todo.description && (
              <p className="text-gray-600 text-sm mt-1 line-clamp-2">{todo.description}</p>
            )}
            
            <div className="flex flex-wrap items-center gap-2 sm:gap-4 mt-2 text-xs text-gray-500">
                {todo.deadline && (
                <div className="flex items-center space-x-1">
                  <Calendar className="w-3 h-3" />
                  <span>Deadline: {formatDate(todo.deadline)}</span>
                </div>
              )}

              {todo.tags.length > 0 && (
                <div className="flex items-center space-x-1">
                  <Tag className="w-3 h-3" />
                  <div className="flex flex-wrap gap-1">
                    {todo.tags.slice(0, 2).map(tag => (
                      <span key={tag} className="bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded text-xs">
                        #{tag}
                      </span>
                    ))}
                    {todo.tags.length > 2 && (
                      <span className="text-gray-400">+{todo.tags.length - 2} more</span>
                    )}
                  </div>
                </div>
              )}

              {/* Assignee display - only show in team mode */}
              {!isPersonalMode && todo.assigned_to_id && (
                <div className="flex items-center space-x-1">
                  <span className="text-purple-600">👤</span>
                  <span className="text-purple-600">
                    Assigned: {getAssigneeName(todo.assigned_to_id)}
                  </span>
                </div>
              )}

              {/* Scheduled event indicator */}
              {nextEventTime && (
                <div className="flex items-center space-x-1">
                  <Clock className="w-3 h-3 text-indigo-600" />
                  <span className="text-indigo-600">
                    Scheduled: {new Date(nextEventTime).toLocaleDateString()} {new Date(nextEventTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              )}

              {/* Estimated time display */}
              {todo.estimated_time_minutes && (
                <div className="flex items-center space-x-1">
                  <Clock className="w-3 h-3 text-orange-600" />
                  <span className="text-orange-600">
                    Est: {todo.estimated_time_minutes}
                  </span>
                </div>
              )}
              
            </div>
            
            {/* Subtask count at bottom */}
            {hasSubtasks && (
              <div className="mt-2 pt-2 border-t border-gray-100">
                <span className="text-xs text-gray-500">
                  {subtasks.filter(s => s.status === 'completed').length} of {subtasks.length} subtasks completed
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Subtasks */}
      {isExpanded && hasSubtasks && (
        <div className="mt-2 space-y-2">
          {subtasks.map(subtask => (
            <TodoItem
              key={subtask.id}
              todo={subtask}
              onClick={() => handleSubtaskClick(subtask)}
              onStatusChange={(status) => handleSubtaskStatusChange(subtask.id, status)}
              onSubtaskStatusChange={onSubtaskStatusChange}
              onAddChild={onAddChild}
              onSubtaskClick={onSubtaskClick}
              onMakeChild={onMakeChild}
              onDelete={onDelete}
              onStartPomodoro={onStartPomodoro}
              compact={true}
              parentTodo={todo}
              allTodos={allTodos}
              depth={depth + 1}
              isDraggable={isDraggable}
              teamMembers={teamMembers}
              isPersonalMode={isPersonalMode}
            />
          ))}
        </div>
      )}

      {/* Status Context Menu */}
      {showStatusMenu && (
        <div
          className="fixed z-50 bg-white rounded-lg shadow-lg border border-gray-200 py-2 min-w-[160px]"
          style={{
            left: `${statusMenuPosition.x}px`,
            top: `${statusMenuPosition.y}px`,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {Object.values(TaskStatus).map((status) => {
            const statusNames = {
              [TaskStatus.NOT_STARTED]: 'Not Started',
              [TaskStatus.IN_PROGRESS]: 'In Progress',
              [TaskStatus.WAITING]: 'Waiting',
              [TaskStatus.COMPLETED]: 'Completed',
              [TaskStatus.CANCELED]: 'Canceled'
            };

            const statusIcons = {
              [TaskStatus.NOT_STARTED]: <Circle className="w-4 h-4 text-gray-400" />,
              [TaskStatus.IN_PROGRESS]: <Clock className="w-4 h-4 text-blue-600" />,
              [TaskStatus.WAITING]: <AlertCircle className="w-4 h-4 text-yellow-600" />,
              [TaskStatus.COMPLETED]: <CheckCircle className="w-4 h-4 text-green-600" />,
              [TaskStatus.CANCELED]: <XCircle className="w-4 h-4 text-red-400" />
            };

            return (
              <button
                key={status}
                onClick={() => handleStatusSelect(status)}
                className={`w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center space-x-3 transition-colors ${
                  todo.status === status ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                }`}
              >
                {statusIcons[status]}
                <span className="text-sm">{statusNames[status]}</span>
                {todo.status === status && (
                  <span className="ml-auto text-xs text-blue-600">Current</span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Memoize to prevent unnecessary re-renders when parent component updates
const TodoItem = React.memo(TodoItemComponent, (prevProps, nextProps) => {
  // Custom comparison - only re-render if these props change
  return (
    prevProps.todo.id === nextProps.todo.id &&
    prevProps.todo.status === nextProps.todo.status &&
    prevProps.todo.title === nextProps.todo.title &&
    prevProps.todo.horizon === nextProps.todo.horizon &&
    prevProps.todo.deadline === nextProps.todo.deadline &&
    prevProps.todo.tags.length === nextProps.todo.tags.length &&
    prevProps.todo.assigned_to_id === nextProps.todo.assigned_to_id &&
    prevProps.todo.parent_id === nextProps.todo.parent_id &&
    prevProps.compact === nextProps.compact &&
    prevProps.depth === nextProps.depth &&
    prevProps.isDraggable === nextProps.isDraggable &&
    prevProps.isInTodaySection === nextProps.isInTodaySection &&
    prevProps.isPersonalMode === nextProps.isPersonalMode &&
    prevProps.nextEventTime === nextProps.nextEventTime &&
    // Re-render when allTodos changes so subtask expand/count stays current
    (prevProps.allTodos?.length ?? 0) === (nextProps.allTodos?.length ?? 0)
  );
});

export default TodoItem;
